/* eslint-disable @typescript-eslint/no-magic-numbers */
import { addMinutes } from 'date-fns';

import {
    BaccaratOutcome,
    BaccaratPhase,
    CardPosition,
    DealerAssistTableErrorCode,
    NotificationCode,
    NotificationSeverity
} from '../models/communication/baccarat-enums.model';
import { BaccaratEventType } from '../models/communication/baccarat-events.model';
import { BaccaratState } from '../models/communication/baccarat-state.model';
import { YEventPayload } from '../models/y-protocol.model';

export const demoInitialState: BaccaratState = {
    configuration: {
        appVersion: '1.0.0-demo',
        tableId: 'tronius-table-1',
        bettingWindowInSeconds: 3,
        videoStreamId: '2e23782e-8151-4b7f-8ccd-2d9771aa9924'
    },

    roundState: {
        phase: BaccaratPhase.WaitingForCroupier
    },

    flagsState: {
        chatEnabled: true
    },

    chatMessages: [
        {
            content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
            from: 'Billy Bob',
            timestamp: addMinutes(Date.now(), -3).getTime()
        },
        {
            content: 'Nullam id dolor id nibh ultricies vehicula ut id elit. Nullam quis risus eget urna mollis ornare vel eu leo.',
            from: 'Supervisor',
            timestamp: addMinutes(Date.now(), -4).getTime(),
            isSupervisor: true
        },
        {
            content: 'Donec id elit non mi porta gravida at eget metus. Maecenas sed diam eget risus varius blandit.',
            from: 'John Doe',
            timestamp: addMinutes(Date.now(), -5).getTime()
        },
        {
            content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
            from: 'Billy Bob',
            timestamp: addMinutes(Date.now(), -6).getTime()
        },
        {
            content: 'Nullam id dolor id nibh ultricies vehicula ut id elit. Nullam quis risus eget urna mollis ornare vel eu leo.',
            from: 'Supervisor',
            timestamp: addMinutes(Date.now(), -7).getTime(),
            isSupervisor: true
        },
        {
            content: 'Donec id elit non mi porta gravida at eget metus. Maecenas sed diam eget risus varius blandit.',
            from: 'John Doe',
            timestamp: addMinutes(Date.now(), -8).getTime()
        },
        {
            content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
            from: 'Billy Bob',
            timestamp: addMinutes(Date.now(), -9).getTime()
        },
        {
            content: 'Nullam id dolor id nibh ultricies vehicula ut id elit. Nullam quis risus eget urna mollis ornare vel eu leo.',
            from: 'Supervisor',
            timestamp: addMinutes(Date.now(), -10).getTime(),
            isSupervisor: true
        },
        {
            content: 'Donec id elit non mi porta gravida at eget metus. Maecenas sed diam eget risus varius blandit.',
            from: 'John Doe',
            timestamp: addMinutes(Date.now(), -11).getTime()
        }
    ]
};

export const demoEvents: Array<Omit<YEventPayload<BaccaratEventType>, 'triggered'>> = [
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.WaitingForCroupier
        }
    },
    {
        name: BaccaratEventType.LoginChange,
        data: {
            new: {
                id: 'croupier-id-123',
                name: 'Cardie Dee'
            }
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.WaitingForPlayers
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.RoundVoid
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.RoundError,
            errorCode: DealerAssistTableErrorCode.BackendDisconnected
        }
    },
    {
        name: BaccaratEventType.Notification,
        data: {
            code: NotificationCode.ScannerDisconnected,
            severity: NotificationSeverity.Warning
        }
    },
    {
        name: BaccaratEventType.Notification,
        data: {
            code: NotificationCode.ScannerConnected,
            severity: NotificationSeverity.Info
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.ShoeChange
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.CardBurn
        }
    },
    {
        name: BaccaratEventType.CardBurn,
        data: {
            card: 208,
            burnedCount: 0,
            totalToBurnCount: 8
        }
    },
    {
        name: BaccaratEventType.CardBurn,
        data: {
            card: 208,
            burnedCount: 1,
            totalToBurnCount: 8
        }
    },
    {
        name: BaccaratEventType.CardBurn,
        data: {
            card: 208,
            burnedCount: 2,
            totalToBurnCount: 8
        }
    },
    {
        name: BaccaratEventType.CardBurn,
        data: {
            card: 208,
            burnedCount: 8,
            totalToBurnCount: 8
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.NewRoundPreparation,
            roundId: 3 + Math.round(Math.random() * 100)
        }
    },
    {
        name: BaccaratEventType.PlayerCountChange,
        data: {
            count: 10 + Math.round(Math.random() * 100)
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.BetsOpen
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.BetsClosed
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.DealingCards
        }
    },
    {
        name: BaccaratEventType.CardDealt,
        data: {
            card: 213,
            cardPosition: CardPosition.PlayerFirstCard,
            bankerHandValue: 0,
            playerHandValue: 0,
            nextCardPosition: CardPosition.BankerFirstCard
        }
    },
    {
        name: BaccaratEventType.CardDealt,
        data: {
            card: 303,
            cardPosition: CardPosition.BankerFirstCard,
            playerHandValue: 0,
            bankerHandValue: 3,
            nextCardPosition: CardPosition.PlayerSecondCard
        }
    },
    {
        name: BaccaratEventType.CardDealt,
        data: {
            card: 407,
            cardPosition: CardPosition.PlayerSecondCard,
            playerHandValue: 7,
            bankerHandValue: 3,
            nextCardPosition: CardPosition.BankerSecondCard
        }
    },
    {
        name: BaccaratEventType.CutCardDrawn
    },
    {
        name: BaccaratEventType.CardDealt,
        data: {
            card: 110,
            cardPosition: CardPosition.BankerSecondCard,
            playerHandValue: 7,
            bankerHandValue: 3,
            nextCardPosition: CardPosition.PlayerThirdCard
        }
    },
    {
        name: BaccaratEventType.ChatMessage,
        data: {
            content: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam id dolor id nibh ultricies vehicula',
            from: 'Dick Tracy',
            timestamp: Date.now()
        }
    },
    {
        name: BaccaratEventType.CardDealt,
        data: {
            card: 201,
            cardPosition: CardPosition.PlayerThirdCard,
            playerHandValue: 8,
            bankerHandValue: 3,
            nextCardPosition: CardPosition.BankerThirdCard
        }
    },
    {
        name: BaccaratEventType.CardDealt,
        data: {
            card: 304,
            cardPosition: CardPosition.BankerThirdCard,
            playerHandValue: 8,
            bankerHandValue: 7
        }
    },
    {
        name: BaccaratEventType.GamePhaseChange,
        data: {
            phase: BaccaratPhase.RoundEnd,
            outcome: BaccaratOutcome.PlayerWin
        }
    }
];
